var _handleActions;

/**
 * Routing
 *
 * The basic actions of routing are:
 * - Select
 * - Next
 * - Previous
 * - Deselect
 *
 * This file defines all of the constants and actions for
 * triggering and setting the state for routing.
 *
 * Each individual resource should add support for next / previous
 * to their redux sagas, this may also be context specific, so is out
 * of the scope of this. They should end up dispatching a select when
 * next or previous is triggered.
 */
import { createAction, handleActions } from 'redux-actions';
import update from 'immutability-helper';

// Collections
var ROUTING_SELECT_COLLECTION = 'ROUTING_SELECT_COLLECTION';
var ROUTING_NEXT_COLLECTION = 'ROUTING_NEXT_COLLECTION';
var ROUTING_PREVIOUS_COLLECTION = 'ROUTING_PREVIOUS_COLLECTION';
var ROUTING_DESELECT_COLLECTION = 'ROUTING_DESELECT_COLLECTION';

// Manifests
var ROUTING_SELECT_MANIFEST = 'ROUTING_SELECT_MANIFEST';
var ROUTING_NEXT_MANIFEST = 'ROUTING_NEXT_MANIFEST';
var ROUTING_PREVIOUS_MANIFEST = 'ROUTING_PREVIOUS_MANIFEST';
var ROUTING_DESELECT_MANIFEST = 'ROUTING_DESELECT_MANIFEST';

// Sequence
var ROUTING_SELECT_SEQUENCE = 'ROUTING_SELECT_SEQUENCE';
var ROUTING_NEXT_SEQUENCE = 'ROUTING_NEXT_SEQUENCE';
var ROUTING_PREVIOUS_SEQUENCE = 'ROUTING_PREVIOUS_SEQUENCE';
var ROUTING_DESELECT_SEQUENCE = 'ROUTING_DESELECT_SEQUENCE';

// Canvas
var ROUTING_SELECT_CANVAS = 'ROUTING_SELECT_CANVAS';
var ROUTING_NEXT_CANVAS = 'ROUTING_NEXT_CANVAS';
var ROUTING_PREVIOUS_CANVAS = 'ROUTING_PREVIOUS_CANVAS';
var ROUTING_DESELECT_CANVAS = 'ROUTING_DESELECT_CANVAS';

// Annotation list
var ROUTING_SELECT_ANNOTATION_LIST = 'ROUTING_SELECT_ANNOTATION_LIST';
var ROUTING_NEXT_ANNOTATION_LIST = 'ROUTING_NEXT_ANNOTATION_LIST';
var ROUTING_PREVIOUS_ANNOTATION_LIST = 'ROUTING_PREVIOUS_ANNOTATION_LIST';
var ROUTING_DESELECT_ANNOTATION_LIST = 'ROUTING_DESELECT_ANNOTATION_LIST';

// Annotation
var ROUTING_SELECT_ANNOTATION = 'ROUTING_SELECT_ANNOTATION';
var ROUTING_NEXT_ANNOTATION = 'ROUTING_NEXT_ANNOTATION';
var ROUTING_PREVIOUS_ANNOTATION = 'ROUTING_PREVIOUS_ANNOTATION';
var ROUTING_DESELECT_ANNOTATION = 'ROUTING_DESELECT_ANNOTATION';

// Range
var ROUTING_SELECT_RANGE = 'ROUTING_SELECT_RANGE';
var ROUTING_NEXT_RANGE = 'ROUTING_NEXT_RANGE';
var ROUTING_PREVIOUS_RANGE = 'ROUTING_PREVIOUS_RANGE';
var ROUTING_DESELECT_RANGE = 'ROUTING_DESELECT_RANGE';

// Layer
var ROUTING_SELECT_LAYER = 'ROUTING_SELECT_LAYER';
var ROUTING_NEXT_LAYER = 'ROUTING_NEXT_LAYER';
var ROUTING_PREVIOUS_LAYER = 'ROUTING_PREVIOUS_LAYER';
var ROUTING_DESELECT_LAYER = 'ROUTING_DESELECT_LAYER';

// Image resource
var ROUTING_SELECT_IMAGE_RESOURCE = 'ROUTING_SELECT_IMAGE_RESOURCE';
var ROUTING_NEXT_IMAGE_RESOURCE = 'ROUTING_NEXT_IMAGE_RESOURCE';
var ROUTING_PREVIOUS_IMAGE_RESOURCE = 'ROUTING_PREVIOUS_IMAGE_RESOURCE';
var ROUTING_DESELECT_IMAGE_RESOURCE = 'ROUTING_DESELECT_IMAGE_RESOURCE';

var DEFAULT_STATE = {
  currentSequence: null,
  currentManifest: null,
  currentCanvas: null,
  currentAnnotationList: null,
  currentAnnotation: null,
  currentRange: null,
  currentLayer: null,
  currentImageResource: null,
  currentCollection: null
};

var selectCollection = createAction(ROUTING_SELECT_COLLECTION);
var nextCollection = createAction(ROUTING_NEXT_COLLECTION);
var previousCollection = createAction(ROUTING_PREVIOUS_COLLECTION);
var deselectCollection = createAction(ROUTING_DESELECT_COLLECTION);
var selectManifest = createAction(ROUTING_SELECT_MANIFEST);
var nextManifest = createAction(ROUTING_NEXT_MANIFEST);
var previousManifest = createAction(ROUTING_PREVIOUS_MANIFEST);
var deselectManifest = createAction(ROUTING_DESELECT_MANIFEST);
var selectSequence = createAction(ROUTING_SELECT_SEQUENCE);
var nextSequence = createAction(ROUTING_NEXT_SEQUENCE);
var previousSequence = createAction(ROUTING_PREVIOUS_SEQUENCE);
var deselectSequence = createAction(ROUTING_DESELECT_SEQUENCE);
var selectCanvas = createAction(ROUTING_SELECT_CANVAS);
var nextCanvas = createAction(ROUTING_NEXT_CANVAS);
var previousCanvas = createAction(ROUTING_PREVIOUS_CANVAS);
var deselectCanvas = createAction(ROUTING_DESELECT_CANVAS);
var selectAnnotationList = createAction(ROUTING_SELECT_ANNOTATION_LIST);
var nextAnnotationList = createAction(ROUTING_NEXT_ANNOTATION_LIST);
var previousAnnotationList = createAction(ROUTING_PREVIOUS_ANNOTATION_LIST);
var deselectAnnotationList = createAction(ROUTING_DESELECT_ANNOTATION_LIST);
var selectAnnotation = createAction(ROUTING_SELECT_ANNOTATION);
var nextAnnotation = createAction(ROUTING_NEXT_ANNOTATION);
var previousAnnotation = createAction(ROUTING_PREVIOUS_ANNOTATION);
var deselectAnnotation = createAction(ROUTING_DESELECT_ANNOTATION);
var selectRange = createAction(ROUTING_SELECT_RANGE);
var nextRange = createAction(ROUTING_NEXT_RANGE);
var previousRange = createAction(ROUTING_PREVIOUS_RANGE);
var deselectRange = createAction(ROUTING_DESELECT_RANGE);
var selectLayer = createAction(ROUTING_SELECT_LAYER);
var nextLayer = createAction(ROUTING_NEXT_LAYER);
var previousLayer = createAction(ROUTING_PREVIOUS_LAYER);
var deselectLayer = createAction(ROUTING_DESELECT_LAYER);
var selectImageResource = createAction(ROUTING_SELECT_IMAGE_RESOURCE);
var nextImageResource = createAction(ROUTING_NEXT_IMAGE_RESOURCE);
var previousImageResource = createAction(ROUTING_PREVIOUS_IMAGE_RESOURCE);
var deselectImageResource = createAction(ROUTING_DESELECT_IMAGE_RESOURCE);

var selectUpdater = function selectUpdater(resource) {
  return function (state, _ref) {
    var _update;

    var payload = _ref.payload;
    return update(state, (_update = {}, _update['current' + resource] = { $set: payload.id }, _update));
  };
};

var deselectUpdater = function deselectUpdater(resource) {
  return function (state) {
    var _update2;

    return update(state, (_update2 = {}, _update2['current' + resource] = { $set: null }, _update2));
  };
};

var reducer = handleActions((_handleActions = {}, _handleActions[selectCollection] = selectUpdater('Collection'), _handleActions[deselectCollection] = deselectUpdater('Collection'), _handleActions[selectManifest] = selectUpdater('Manifest'), _handleActions[deselectManifest] = deselectUpdater('Manifest'), _handleActions[selectSequence] = selectUpdater('Sequence'), _handleActions[deselectSequence] = deselectUpdater('Sequence'), _handleActions[selectCanvas] = selectUpdater('Canvas'), _handleActions[deselectCanvas] = deselectUpdater('Canvas'), _handleActions[selectAnnotationList] = selectUpdater('AnnotationList'), _handleActions[deselectAnnotationList] = deselectUpdater('AnnotationList'), _handleActions[selectAnnotation] = selectUpdater('Annotation'), _handleActions[deselectAnnotation] = deselectUpdater('Annotation'), _handleActions[selectRange] = selectUpdater('Range'), _handleActions[deselectRange] = deselectUpdater('Range'), _handleActions[selectLayer] = selectUpdater('Layer'), _handleActions[deselectLayer] = deselectUpdater('Layer'), _handleActions[selectImageResource] = selectUpdater('ImageResource'), _handleActions[deselectImageResource] = deselectUpdater('ImageResource'), _handleActions), DEFAULT_STATE);

export { reducer, DEFAULT_STATE,
// Actions.
selectCollection, nextCollection, previousCollection, deselectCollection, selectManifest, nextManifest, previousManifest, deselectManifest, selectSequence, nextSequence, previousSequence, deselectSequence, selectCanvas, nextCanvas, previousCanvas, deselectCanvas, selectAnnotationList, nextAnnotationList, previousAnnotationList, deselectAnnotationList, selectAnnotation, nextAnnotation, previousAnnotation, deselectAnnotation, selectRange, nextRange, previousRange, deselectRange, selectLayer, nextLayer, previousLayer, deselectLayer, selectImageResource, nextImageResource, previousImageResource, deselectImageResource, ROUTING_SELECT_COLLECTION, ROUTING_NEXT_COLLECTION, ROUTING_PREVIOUS_COLLECTION, ROUTING_DESELECT_COLLECTION, ROUTING_SELECT_MANIFEST, ROUTING_NEXT_MANIFEST, ROUTING_PREVIOUS_MANIFEST, ROUTING_DESELECT_MANIFEST, ROUTING_SELECT_SEQUENCE, ROUTING_NEXT_SEQUENCE, ROUTING_PREVIOUS_SEQUENCE, ROUTING_DESELECT_SEQUENCE, ROUTING_SELECT_CANVAS, ROUTING_NEXT_CANVAS, ROUTING_PREVIOUS_CANVAS, ROUTING_DESELECT_CANVAS, ROUTING_SELECT_ANNOTATION_LIST, ROUTING_NEXT_ANNOTATION_LIST, ROUTING_PREVIOUS_ANNOTATION_LIST, ROUTING_DESELECT_ANNOTATION_LIST, ROUTING_SELECT_ANNOTATION, ROUTING_NEXT_ANNOTATION, ROUTING_PREVIOUS_ANNOTATION, ROUTING_DESELECT_ANNOTATION, ROUTING_SELECT_RANGE, ROUTING_NEXT_RANGE, ROUTING_PREVIOUS_RANGE, ROUTING_DESELECT_RANGE, ROUTING_SELECT_LAYER, ROUTING_NEXT_LAYER, ROUTING_PREVIOUS_LAYER, ROUTING_DESELECT_LAYER, ROUTING_SELECT_IMAGE_RESOURCE, ROUTING_NEXT_IMAGE_RESOURCE, ROUTING_PREVIOUS_IMAGE_RESOURCE, ROUTING_DESELECT_IMAGE_RESOURCE,
// Misc
selectUpdater, deselectUpdater };